import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  uploadExcel,
  uploadSitemap,
  fetchJobStatus,
} from '../api/similarity';
import styles from './SimilarityTab.module.css';
import type { JobStatus } from '../api/types';
import { FiUpload, FiRefreshCw, FiDownload } from 'react-icons/fi';

export default function SimilarityTab() {
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<'excel' | 'sitemap'>('excel');
  const [jobId, setJobId] = useState<string | null>(null);

  // Mutation to start the job
  const createMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('No file selected');
      return mode === 'excel'
        ? uploadExcel(file)
        : uploadSitemap(file);
    },
    onSuccess(data: JobStatus) {
      setJobId(data.id);
    },
  });

  // Query to poll job status every 5s once we have a jobId
  const {
    data: status,
    isLoading: statusLoading,
    isError: statusError,
    refetch,
  } = useQuery<JobStatus>({
    queryKey: ['job', jobId],
    queryFn: () => fetchJobStatus(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      // Stop polling if job is finished, failed, or has error
      const data = query.state.data;
      if (data?.status === 'finished' || data?.status === 'failed' || data?.error) {
        return false; // Disable polling
      }
      return 5000; // Continue polling every 5 seconds
    },
  });

  // Construct full download URL
  const downloadUrl =
    status?.download_url
              ? `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/similarity${status.download_url}`
      : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate();
  };

  return (
    <div className={styles.wrapper}>
      <h2>Similarity Check</h2>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.radioGroup}>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="mode"
              value="excel"
              checked={mode === 'excel'}
              onChange={() => setMode('excel')}
            />
            Excel
          </label>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="mode"
              value="sitemap"
              checked={mode === 'sitemap'}
              onChange={() => setMode('sitemap')}
            />
            Sitemap XML
          </label>
        </div>

        <input
          className={styles.fileInput}
          type="file"
          accept={mode === 'excel' ? '.xlsx,.xls' : '.xml'}
          onChange={e => setFile(e.target.files?.[0] ?? null)}
        />

        <button
          className={styles.btn}
          type="submit"
          disabled={createMutation.isPending}
        >
          <FiUpload />
          {createMutation.isPending ? 'Uploading…' : 'Start Job'}
        </button>
      </form>

      {createMutation.isError && (
        <div className={styles.error}>
          Error: {(createMutation.error as Error).message}
        </div>
      )}

      {jobId && (
        <div className={styles.status}>
          <p>
            <strong>Job ID:</strong> {jobId}
          </p>

          {statusLoading ? (
            <p>Checking status…</p>
          ) : statusError ? (
            <p className={styles.error}>
              Error fetching status.{' '}
              <button onClick={() => refetch()}>
                <FiRefreshCw />
              </button>
            </p>
          ) : status ? (
            <>
              <p>
                <strong>Status:</strong> {status.status}
              </p>
              {status.error && (
                <p className={styles.error}>
                  <strong>Error:</strong> {status.error}
                </p>
              )}
              {downloadUrl && (
                <a
                  href={downloadUrl}
                  className={styles.downloadLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FiDownload /> Download Result
                </a>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
);
}
